/**
 * The scenes of the feature tour, for people who joined before the new
 * onboarding (6 Oct). Copy lives in the locales under `tour.scenes.<id>`;
 * photos are the ones the new onboarding uses for its chapter screens.
 */
import photoIntro from '../../images/onboarding-prototype/morning-dog-walk.jpg';
import photoScheduling from '../../images/onboarding-prototype/cc.jpg';
import photoAssistants from '../../images/onboarding-prototype/colleagues.jpg';
// import photoShop from '../../images/onboarding-prototype/shop.jpg';
import photoInbox from '../../images/onboarding-prototype/inbox.jpg';
import photoEvening from '../../images/onboarding-prototype/evening-park.jpg';
import photoTeam from '../../images/organisation/team-tour-mobile.jpg';
import photoTeamWide from '../../images/organisation/team-tour-desktop.jpg';
import wideIntro from '../../images/onboarding-prototype/morning-dog-walk-wide.jpg';
import wideScheduling from '../../images/onboarding-prototype/cc-wide.jpg';
import wideAssistants from '../../images/onboarding-prototype/colleagues-wide.jpg';
// import wideShop from '../../images/onboarding-prototype/shop-wide.jpg';
import wideInbox from '../../images/onboarding-prototype/inbox-wide.jpg';

/** Which scene ends the tour: Organisations for people with a work email connected, the 7pm check-in for everyone else. */
/** `both` (prototype only): the check-in, then Organisations. */
export type TourEnding = 'check_in' | 'organisations' | 'both';

/** A scene the user asked to hear more about; `title` is how the hand-off names it. */
export interface TourTopic {
  id: string;
  title: string;
}

/** Slow settle of the photo after the reveal (start offsets, in % / scale). */
export interface Drift {
  x: number;
  y: number;
  scale: number;
}

export interface TourScene {
  /** Locale key under `tour.scenes`, and the topic id sent to the backend. */
  id: string;
  /** False for the intro: no "Show me after", not counted. */
  feature: boolean;
  photoOrigin: string;
  drift: Drift;
  image: string;
  /** Horizontal focus of the photo (object-position x); default centre. */
  focusX?: string;
  /** Vertical focus of the photo (object-position y); default centre. */
  focusY?: string;
  /** Desktop (16:9 stage). `image` is the landscape version of the photo
   *  (the portrait originals outpainted to 3:2); it is drawn full height
   *  from the left edge, moved by `shift` px so the subject sits in the
   *  photo area. */
  desktop?: { image?: string; shift?: number };
}

const INTRO: TourScene = {
  id: 'intro',
  feature: false,
  photoOrigin: '28% 57%',
  drift: { x: 3, y: 0, scale: 1.12 },
  image: photoIntro,
  desktop: {
    image: wideIntro,
    shift: -80,
  },
};

const FEATURES: TourScene[] = [
  {
    id: 'scheduling',
    feature: true,
    photoOrigin: '45% 41%',
    drift: { x: -3, y: -2, scale: 1.14 },
    image: photoScheduling,
    desktop: {
      image: wideScheduling,
      shift: -140,
    },
  },
  {
    id: 'assistants',
    feature: true,
    photoOrigin: '30% 40%',
    drift: { x: 0, y: 3, scale: 1.1 },
    image: photoAssistants,
    desktop: {
      image: wideAssistants,
      shift: -140,
    },
  },
  // Groceries hidden from the tour for now; restore with the imports above.
  // {
  //   id: 'shop',
  //   feature: true,
  //   photoOrigin: '50% 44%',
  //   drift: { x: 2, y: -3, scale: 1.16 },
  //   image: photoShop,
  //   desktop: {
  //     image: wideShop,
  //     shift: -150,
  //   },
  //   focusY: '28%',
  // },
  {
    id: 'email',
    feature: true,
    photoOrigin: '65% 50%',
    drift: { x: -2, y: 2, scale: 1.12 },
    image: photoInbox,
    desktop: {
      image: wideInbox,
      shift: -140,
    },
  },
];

const CHECK_IN: TourScene = {
  id: 'check_in',
  feature: true,
  photoOrigin: '50% 46%',
  drift: { x: 2, y: 1, scale: 1.1 },
  image: photoEvening,
  focusX: '60%',
  focusY: '22%',
  desktop: {
    shift: -673,
  },
};

const ORGANISATIONS: TourScene = {
  id: 'organisations',
  feature: true,
  photoOrigin: '50% 42%',
  drift: { x: -2, y: 1, scale: 1.1 },
  image: photoTeam,
  focusY: '30%',
  desktop: {
    image: photoTeamWide,
    shift: -350,
  },
};

/** The standalone page's last card: what was covered, then Back to WhatsApp. */
const SUMMARY: TourScene = {
  ...INTRO,
  id: 'summary',
};

/** The intro, three features, the ending scene for this user and, on the
 *  standalone page, the summary card. */
export const scenesFor = (ending: TourEnding, summary = false): TourScene[] => [
  INTRO,
  ...FEATURES,
  ...(ending === 'both'
    ? [CHECK_IN, ORGANISATIONS]
    : [ending === 'organisations' ? ORGANISATIONS : CHECK_IN]),
  ...(summary ? [SUMMARY] : []),
];
